import { useMemo, useState, useEffect } from "react";
import { Landmark } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { fmtDate, rm } from "../lib/format";
import { showQueryLoader } from "../lib/query-utils";
import { toast } from "../stores/toast";
import {
  Badge,
  Button,
  Textarea,
  statusColor,
} from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { useQueryRecordId } from "../lib/use-query-record";

type ReceiptStatus = "PENDING" | "VERIFIED" | "REJECTED";

interface ReceiptRow {
  id: string;
  orderId: string;
  orderNo: string;
  orderKind: string;
  orderTotal: number;
  currency: string;
  customer: string;
  customerEmail: string;
  reference: string;
  amount: number;
  status: ReceiptStatus;
  fileUrl: string;
  fileCount?: number;
  files?: { id: string; fileUrl: string }[];
  rejectReason: string | null;
  createdAt: string;
}

const STATUS_TABS: { value: ReceiptStatus; label: string }[] = [
  { value: "PENDING", label: "Pending" },
  { value: "VERIFIED", label: "Verified" },
  { value: "REJECTED", label: "Rejected" },
];

const KIND_LABEL: Record<string, string> = {
  STORE: "Shop order",
  RAFFLE_WIN: "Raffle win",
  AUCTION_WIN: "Auction win",
  CREDIT_TOPUP: "Credit top-up",
  VAULT_SHIPMENT: "Vault shipment",
};

export function Receipts() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get("status") as ReceiptStatus | null) ?? "PENDING";
  const search = searchParams.get("search") ?? "";

  const [searchInput, setSearchInput] = useState(search);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const { recordId, clearRecordId } = useQueryRecordId();

  const listQuery = useQuery({
    queryKey: ["receipts", status, search],
    queryFn: () => {
      const params = new URLSearchParams({ status });
      if (search.trim()) params.set("search", search.trim());
      return api<{ items: ReceiptRow[] }>(`/payments/receipts?${params}`);
    },
  });

  useEffect(() => {
    if (!recordId) return;
    setSelectedId(recordId);
    clearRecordId();
  }, [recordId, clearRecordId]);

  const reviewMutation = useMutation({
    mutationFn: (args: { id: string; approve: boolean; rejectReason?: string }) =>
      api(`/payments/receipts/${args.id}/review`, {
        method: "POST",
        body: { approve: args.approve, rejectReason: args.rejectReason },
      }),
    onSuccess: (_, vars) => {
      toast("success", vars.approve ? "Receipt verified" : "Receipt rejected");
      setSelectedId(null);
      setRejectReason("");
      void queryClient.invalidateQueries({ queryKey: ["receipts"] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Review failed"),
  });

  const { data: selectedReceipt } = useQuery({
    queryKey: ["receipt", selectedId],
    queryFn: () => api<{ item: ReceiptRow }>(`/payments/receipts/${selectedId}`),
    enabled: !!selectedId && !listQuery.data?.items.some((row) => row.id === selectedId),
  });

  const selected =
    listQuery.data?.items.find((r) => r.id === selectedId) ?? selectedReceipt?.item;

  useEffect(() => {
    const item = selectedReceipt?.item;
    if (!item || item.status === status) return;
    const next = new URLSearchParams(searchParams);
    next.set("status", item.status);
    setSearchParams(next, { replace: true });
  }, [selectedReceipt?.item, status, searchParams, setSearchParams]);

  const columns: Column<ReceiptRow>[] = useMemo(
    () => [
      {
        key: "created",
        header: "Uploaded",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.createdAt,
        exportValue: (r) => fmtDate(r.createdAt),
        render: (r) => <span className="text-xs text-white/50">{fmtDate(r.createdAt)}</span>,
      },
      {
        key: "customer",
        header: "Customer",
        sortable: true,
        sortValue: (r) => r.customer,
        exportValue: (r) => `${r.customer} (${r.customerEmail})`,
        render: (r) => (
          <div>
            <p className="text-sm">{r.customer}</p>
            <p className="text-[10px] text-white/40">{r.customerEmail}</p>
          </div>
        ),
      },
      {
        key: "order",
        header: "For",
        sortable: true,
        sortValue: (r) => r.orderNo,
        exportValue: (r) => `${r.orderNo} (${KIND_LABEL[r.orderKind] ?? r.orderKind})`,
        render: (r) => (
          <div>
            <p className="font-mono text-xs">{r.orderNo}</p>
            <p className="text-[10px] text-white/40">{KIND_LABEL[r.orderKind] ?? r.orderKind}</p>
          </div>
        ),
      },
      {
        key: "amount",
        header: "Amount",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.amount,
        exportValue: (r) => r.amount,
        render: (r) => <span className="font-display font-semibold">{rm(r.amount, r.currency)}</span>,
      },
      {
        key: "ref",
        header: "Reference",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.reference,
        render: (r) => (
          <div>
            <span className="font-mono text-xs text-white/70">{r.reference}</span>
            {(r.fileCount ?? 1) > 1 && (
              <p className="mt-0.5 text-[10px] text-white/40">{r.fileCount} files</p>
            )}
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        sortValue: (r) => r.status,
        render: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
      },
    ],
    [],
  );

  function updateParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={listQuery.data?.items}
        rowKey={(r) => r.id}
        loading={showQueryLoader(listQuery)}
        exportFilename="receipts"
        toolbar={{
          search: {
            value: searchInput,
            onChange: setSearchInput,
            onSubmit: () => updateParams({ search: searchInput.trim() || null }),
            placeholder: "Search reference, order#, customer…",
          },
          views: {
            options: STATUS_TABS,
            value: status,
            onChange: (v) => updateParams({ status: v }),
          },
        }}
        onRowClick={(r) => {
          setSelectedId(r.id);
          setRejectReason("");
        }}
        activeKey={selectedId}
        empty={{
          icon: Landmark,
          title: status === "PENDING" ? "No pending receipts" : `No ${status.toLowerCase()} receipts`,
          hint: search ? "Try clearing search." : undefined,
        }}
      />

      <RightSidebarPanel
        open={!!selectedId && !!selected}
        title={selected?.orderNo ?? "Receipt"}
        subtitle={selected ? `${selected.customer} · ${selected.reference}` : undefined}
        wide
        onClose={() => setSelectedId(null)}
        footer={
          selected?.status === "PENDING" && (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="danger"
                size="sm"
                disabled={reviewMutation.isPending}
                onClick={() =>
                  reviewMutation.mutate({
                    id: selected.id,
                    approve: false,
                    rejectReason: rejectReason.trim() || "Receipt could not be verified",
                  })
                }
              >
                Reject
              </Button>
              <Button
                size="sm"
                disabled={reviewMutation.isPending}
                onClick={() => reviewMutation.mutate({ id: selected.id, approve: true })}
              >
                Approve
              </Button>
            </div>
          )
        }
      >
        {selected && (
          <div className="space-y-5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={statusColor(selected.status)}>{selected.status}</Badge>
              <span className="font-display text-lg font-bold text-brand-pink">
                {rm(selected.amount, selected.currency)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-white/40">Order total</p>
                <p>{rm(selected.orderTotal, selected.currency)}</p>
              </div>
              <div>
                <p className="text-white/40">Uploaded</p>
                <p>{fmtDate(selected.createdAt)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-white/40">Bank reference</p>
                <p className="font-mono">{selected.reference}</p>
              </div>
              <div className="col-span-2">
                <p className="text-white/40">Customer</p>
                <p>{selected.customer}</p>
                <p className="text-white/50">{selected.customerEmail}</p>
              </div>
            </div>

            {selected.rejectReason && selected.status === "REJECTED" && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                {selected.rejectReason}
              </div>
            )}

            <section>
              <h4 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-white/40">
                Receipt{(selected.fileCount ?? selected.files?.length ?? 1) > 1 ? "s" : ""}
                {(selected.fileCount ?? selected.files?.length ?? 1) > 1 && (
                  <span className="ml-2 normal-case text-white/50">
                    ({selected.fileCount ?? selected.files?.length} files)
                  </span>
                )}
              </h4>
              {(selected.files ?? [{ id: selected.id, fileUrl: selected.fileUrl }]).map((file, index) => (
                <div key={file.id} className={index > 0 ? "mt-4 border-t border-line pt-4" : ""}>
                  {(selected.files?.length ?? 0) > 1 && (
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                      File {index + 1}
                    </p>
                  )}
                  <a
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-2 inline-flex text-xs text-brand-pink hover:underline"
                  >
                    Open full receipt ↗
                  </a>
                  {/\.pdf(?:\?|$)/i.test(file.fileUrl) ? (
                    <iframe
                      title={`Payment receipt ${index + 1}`}
                      src={file.fileUrl}
                      className="h-80 w-full rounded-xl border border-line bg-white"
                    />
                  ) : (
                    <img
                      src={file.fileUrl}
                      alt={`Payment receipt ${index + 1}`}
                      className="max-h-80 w-full rounded-xl border border-line object-contain bg-black/20"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  )}
                </div>
              ))}
            </section>

            {selected.status === "PENDING" && (
              <Textarea
                label="Reject reason"
                placeholder="Explain why this receipt cannot be verified"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            )}
          </div>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
